import {useRef} from "react";
import {useApolloClient} from "@apollo/client";
import {Moment} from "moment";
import {ArrayDataOptions, SortOrder} from "../../../../generated/graphql";
import {PAYMENT_EXPORT_CONFIG, calculateChunkCount, calculateProgress, shouldUseChunkedExport} from "../utils/exportConfig";
import {CsvRowData, ExportConfig} from "../types";
import {useExportPaymentsState} from "./useExportPaymentsState";

type UseExportPaymentsLogicProps = {
    options?: ArrayDataOptions | null;
    dateRange: {start: Moment; end: Moment};
    respectFilters: boolean;
    exportConfig: ExportConfig;
};

export const useExportPaymentsLogic = ({options, dateRange, respectFilters, exportConfig}: UseExportPaymentsLogicProps) => {
    const apolloClient = useApolloClient();
    const exportStateHook = useExportPaymentsState(exportConfig.entityLabel);
    // Abbrechen/Schließen erhöht die Lauf-ID: Ein noch ladender alter Lauf darf danach weder Daten noch
    // Erfolg/Fehler in einen neu gestarteten Export schreiben (#1544, lokaler Review).
    const runId = useRef(0);
    const resetExport = () => {
        runId.current++;
        exportStateHook.resetExport();
    };

    const sortField = exportConfig.sortField || exportConfig.dateFieldKey;

    const createExportOptions = (offset?: number, limit?: number): ArrayDataOptions => {
        // Den Tabellenfilter auf dieselbe Datumsspalte ersetzt der gewählte Zeitraum (doppelter Filter = doppelter Join).
        const baseFilters = respectFilters && options && options.filter ? options.filter.filter((f) => f.column !== exportConfig.dateFieldKey) : [];

        const dateFilter = {
            column: exportConfig.dateFieldKey,
            // Untergrenze = lokaler Tagesanfang, Obergrenze = lokales TagesENDE (nicht bloss "YYYY-MM-DD"):
            // Der Server vergleicht inklusiv (column <= endDate). Ein bloss als Kalendertag gesendetes Enddatum
            // wird zu 00:00 Uhr gecastet, wodurch ALLE am letzten Tag erfassten Zahlungen aus Export UND Zähler
            // fielen. Gleiche Serialisierung wie Offene-Posten-Liste / RefundsList.
            values: [dateRange.start.clone().startOf("day").toISOString(), dateRange.end.clone().endOf("day").toISOString()],
            table: exportConfig.dateFieldTable,
        };

        const effectiveLimit = limit !== undefined ? limit : PAYMENT_EXPORT_CONFIG.MAX_EXPORT_LIMIT;
        const effectiveOffset = offset !== undefined ? offset : 0;

        const shouldSortByDateField = !respectFilters || !options || !options.sortColumn;
        const sortColumn = shouldSortByDateField ? sortField : options?.sortColumn;
        const sortOrder = shouldSortByDateField ? SortOrder.Ascending : options?.sortOrder;

        return {
            filter: [...baseFilters, dateFilter],
            sortColumn: sortColumn,
            sortOrder: sortOrder,
            offset: effectiveOffset,
            limit: effectiveLimit,
        };
    };

    /**
     * Führt einen einzelnen Chunk-Export durch
     */
    const executeChunkExport = async (chunkIndex: number, totalCount: number): Promise<Array<CsvRowData>> => {
        const offset = chunkIndex * PAYMENT_EXPORT_CONFIG.CHUNK_SIZE;
        const limit = PAYMENT_EXPORT_CONFIG.CHUNK_SIZE;

        const chunkOptions = createExportOptions(offset, limit);

        const result = await apolloClient.query({
            query: exportConfig.query,
            variables: exportConfig.buildVariables(chunkOptions),
            fetchPolicy: "no-cache",
        });

        if (!result || !result.data) {
            throw new Error(`Chunk ${chunkIndex + 1}: Keine Daten erhalten`);
        }

        const extracted = exportConfig.getItemsFromResult(result.data);
        if (!extracted.items) {
            throw new Error(`Chunk ${chunkIndex + 1}: Keine Daten erhalten`);
        }

        return extracted.items as any;
    };

    /**
     * Chunked Export - für große Datenmengen
     */
    const handleChunkedExport = async (totalCount: number) => {
        const run = ++runId.current;
        const superseded = () => runId.current !== run;

        try {
            const fileName = `${exportConfig.fileNamePrefix}-${dateRange.start.format("YYYY-MM-DD")}-${dateRange.end.format("YYYY-MM-DD")}.csv`;

            exportStateHook.showExportModal(fileName, totalCount);

            const allCsvData: Array<CsvRowData> = [];
            const chunks = calculateChunkCount(totalCount);

            for (let chunkIndex = 0; chunkIndex < chunks; chunkIndex++) {
                // Ein gescheiterter Teil bricht den ganzen Export ab: Eine lückenhafte Datei darf nicht als Erfolg
                // zum Download stehen (#1544).
                // eslint-disable-next-line no-await-in-loop
                const chunkData = await executeChunkExport(chunkIndex, totalCount);

                if (superseded()) {
                    return;
                }
                // Leere Seite: Der Bestand ist seit der Zählung geschrumpft. Grenze der Offset-Pagination: Schrumpft
                // er vor dem aktuellen Offset, rutschen spätere Zeilen nach vorn und fehlen unbemerkt.
                if (chunkData.length === 0) {
                    break;
                }

                allCsvData.push(...chunkData);

                exportStateHook.updateProgress(calculateProgress(chunkIndex, totalCount));

                exportStateHook.setExportData([...allCsvData]);

                if (chunkIndex < chunks - 1) {
                    // eslint-disable-next-line no-await-in-loop
                    await new Promise((resolve) => setTimeout(resolve, PAYMENT_EXPORT_CONFIG.CHUNK_DELAY_MS));
                    if (superseded()) {
                        return;
                    }
                }
            }

            if (allCsvData.length === 0) {
                exportStateHook.handleExportError("Keine Daten zum Exportieren gefunden");
                return;
            }
            exportStateHook.handleExportSuccess(allCsvData.length);
        } catch (error) {
            if (superseded()) {
                return;
            }
            console.error("Chunked export error:", error);
            exportStateHook.handleExportError("Export abgebrochen: Ein Teil der Daten konnte nicht geladen werden. Bitte erneut versuchen.");
        }
    };

    /**
     * Einfacher Export - für kleinere Datenmengen
     */
    const handleSimpleExport = async () => {
        const run = ++runId.current;

        try {
            const fileName = `${exportConfig.fileNamePrefix}-${dateRange.start.format("YYYY-MM-DD")}-${dateRange.end.format("YYYY-MM-DD")}.csv`;

            exportStateHook.showExportModal(fileName);

            const exportOptions = createExportOptions();

            const result = await apolloClient.query({
                query: exportConfig.query,
                variables: exportConfig.buildVariables(exportOptions),
                fetchPolicy: "no-cache",
            });

            if (runId.current !== run) {
                return;
            }
            if (result && result.data) {
                const extracted = exportConfig.getItemsFromResult(result.data);
                if (extracted.items && extracted.items.length > 0) {
                    exportStateHook.setExportData(extracted.items as any);
                    exportStateHook.handleExportSuccess(extracted.items.length);
                } else {
                    exportStateHook.handleExportError("Keine Daten zum Exportieren gefunden");
                }
            } else {
                exportStateHook.handleExportError("Keine Daten zum Exportieren gefunden");
            }
        } catch (error) {
            if (runId.current !== run) {
                return;
            }
            console.error("Simple export error:", error);
            exportStateHook.handleExportError("Fehler beim Export");
        }
    };

    /**
     * Hauptexport-Handler - entscheidet zwischen einfach und chunked
     */
    const handleExport = (totalCount: number) => {
        if (shouldUseChunkedExport(totalCount)) {
            handleChunkedExport(totalCount);
        } else {
            handleSimpleExport();
        }
    };

    return {
        ...exportStateHook,
        resetExport,
        handleExport,
        exportLoading: exportStateHook.isExporting,
        shouldUseChunkedExport,
    };
};
